import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../context/AuthContext";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

export default function Clients() {
  const { isTeamLeader, isSuperAdmin } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get("/api/clients"),
      API.get("/api/settings/industries"),
    ]).then(([c, i]) => {
      setClients([...c.data].sort((a, b) => a.name.localeCompare(b.name, "es")));
      setIndustries(i.data);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = clients.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase());
    const matchIndustry = !filterIndustry || c.industry === filterIndustry;
    return matchSearch && matchIndustry;
  });

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}" permanentemente?`)) return;
    try {
      await API.delete(`/api/clients/${id}`);
      setClients(prev => prev.filter(c => c.id !== id));
      toast("Cliente eliminado", "success");
    } catch {
      toast("Error al eliminar", "error");
    }
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h1 className="page-title">Clientes</h1>
            <p className="page-subtitle">{clients.length} clientes en total</p>
          </div>
          {isTeamLeader && (
            <div className="page-actions">
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                + Nuevo cliente
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* Filters */}
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <input className="input" placeholder="Buscar cliente..." value={search}
            onChange={e => setSearch(e.target.value)} style={{ maxWidth: 280, flex: 1, minWidth: 140 }} />
          <select className="select" value={filterIndustry}
            onChange={e => setFilterIndustry(e.target.value)} style={{ maxWidth: 200, flex: 1, minWidth: 140 }}>
            <option value="">Todos los rubros</option>
            {industries.map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
          </select>
        </div>

        {/* List */}
        {loading ? (
          <div style={{ textAlign: "center", padding: 48 }}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <h3>No hay clientes</h3>
            <p>{search ? "Ningún cliente coincide con la búsqueda" : "Creá el primer cliente"}</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filtered.map(client => (
              <ClientCard
                key={client.id}
                client={client}
                isTeamLeader={isTeamLeader}
                isSuperAdmin={isSuperAdmin}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <NewClientModal
          industries={industries}
          onClose={() => setShowModal(false)}
          onCreated={client => {
            setClients(prev => [client, ...prev]);
            setShowModal(false);
            toast("Cliente creado", "success");
            navigate(`/clients/${client.id}`);
          }}
        />
      )}
    </div>
  );
}

function ClientCard({ client, isTeamLeader, isSuperAdmin, onDelete }) {
  return (
    <div className="card client-card" style={{ padding: "10px 20px" }}>
      {/* Single row on desktop — wraps to 2 rows on mobile */}
      <div className="client-card-row">
        <div style={{
          width: 36, height: 36, borderRadius: "var(--radius-md)",
          background: client.primary_color || "#0b00f9",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 700, color: "#fff", flexShrink: 0,
        }}>
          {client.name[0].toUpperCase()}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {client.name}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{client.industry || "Sin rubro"}</div>
        </div>

        <span className={`badge ${client.status === "active" ? "badge-green" : "badge-gray"}`} style={{ flexShrink: 0 }}>
          {client.status === "active" ? "Activo" : "Inactivo"}
        </span>

        <div className="client-card-actions">
          <Link to={`/clients/${client.id}`} className="client-btn-primary">
            <button className="btn btn-secondary btn-sm">Ver perfil</button>
          </Link>
          {isSuperAdmin && (
            <button className="btn btn-danger btn-sm client-btn-danger" onClick={() => onDelete(client.id, client.name)}>
              Eliminar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Section label helper ───────────────────────────────────────────────── */
function SectionHeading({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase",
      color: "var(--text-muted)", marginBottom: 12, marginTop: 4,
      paddingBottom: 8, borderBottom: "1px solid var(--border)",
    }}>
      {children}
    </div>
  );
}

/* ─── New Client Modal ───────────────────────────────────────────────────── */
function NewClientModal({ industries, onClose, onCreated }) {
  const { isSuperAdmin } = useAuth();
  const toast = useToast();
  const [form, setForm] = useState({
    name: "", industry: "", primary_color: "#0b00f9",
    logo_url: "", drive_folder_url: "", reports_drive_folder_url: "",
    monthly_limit_usd: "",
  });
  const [saving, setSaving] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  useEffect(() => {
    API.get("/api/clients/meta/assignable-users")
      .then(r => setAllUsers(r.data.filter(u => u.role === "member")))
      .catch(() => {});
  }, []);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const toggleUser = (id) => setSelectedUserIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast("El nombre es requerido", "error");
    setSaving(true);
    try {
      const r = await API.post("/api/clients", {
        name:                     form.name.trim(),
        industry:                 form.industry || undefined,
        primary_color:            form.primary_color,
        logo_url:                 form.logo_url || undefined,
        drive_folder_url:         form.drive_folder_url || undefined,
        reports_drive_folder_url: form.reports_drive_folder_url || undefined,
        monthly_limit_usd:        form.monthly_limit_usd || undefined,
        userIds:                  [...selectedUserIds],
      });
      onCreated(r.data);
    } catch (err) {
      toast(err.response?.data?.error || "Error al crear cliente", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={e => e.target === e.currentTarget && !saving && onClose()}
      className="new-client-overlay"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        zIndex: 1000, overflowY: "auto", padding: "24px 16px",
      }}
    >
      <div className="card new-client-modal" style={{
        width: "100%", maxWidth: 600, padding: 0, overflow: "hidden",
        margin: "auto",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px 16px", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h2 style={{ fontSize: 17, fontWeight: 600 }}>Nuevo cliente</h2>
          <button onClick={onClose} disabled={saving} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", fontSize: 18, lineHeight: 1, padding: 4,
          }}>✕</button>
        </div>

        {/* Body — scrollable */}
        <div style={{ padding: "20px 24px", overflowY: "auto", maxHeight: "70vh" }}>

          {/* ── Información básica ── */}
          <SectionHeading>Información básica</SectionHeading>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }} className="modal-2col">
            <div className="field">
              <label className="label">Nombre *</label>
              <input className="input" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Nombre del cliente" />
            </div>
            <div className="field">
              <label className="label">Rubro</label>
              <select className="select" value={form.industry} onChange={e => set("industry", e.target.value)}>
                <option value="">Seleccioná un rubro</option>
                {industries.map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "0 12px", alignItems: "end" }} className="field">
            <label className="label" style={{ gridColumn: "1 / -1" }}>Color primario</label>
            <input type="color" value={form.primary_color} onChange={e => set("primary_color", e.target.value)}
              style={{ width: 44, height: 40, border: "1px solid var(--border)", borderRadius: "var(--radius-md)", background: "none", cursor: "pointer", padding: 2 }} />
            <input className="input" value={form.primary_color} onChange={e => set("primary_color", e.target.value)} />
          </div>

          <div className="field">
            <label className="label">URL del Logo <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(opcional)</span></label>
            <input className="input" value={form.logo_url} onChange={e => set("logo_url", e.target.value)} placeholder="https://..." />
          </div>

          {/* ── Drive ── */}
          <SectionHeading>Google Drive</SectionHeading>
          <div className="field">
            <label className="label">Carpeta de documentos</label>
            <input className="input" value={form.drive_folder_url} onChange={e => set("drive_folder_url", e.target.value)} placeholder="https://drive.google.com/drive/folders/..." />
            <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Los agentes la leen para generar el perfil estratégico del cliente.</p>
          </div>
          <div className="field">
            <label className="label">Carpeta de informes</label>
            <input className="input" value={form.reports_drive_folder_url} onChange={e => set("reports_drive_folder_url", e.target.value)} placeholder="https://drive.google.com/drive/folders/..." />
            <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Los agentes consultan esta carpeta para análisis y reportes.</p>
          </div>

          {/* ── Límite mensual (super_admin) ── */}
          {isSuperAdmin && (
            <>
              <SectionHeading>Límite de consumo</SectionHeading>
              <div className="field">
                <label className="label">Límite mensual de API (USD) <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(opcional)</span></label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14, color: "var(--text-muted)" }}>$</span>
                  <input className="input" type="number" min="0" step="0.01" placeholder="Sin límite"
                    value={form.monthly_limit_usd} onChange={e => set("monthly_limit_usd", e.target.value)} style={{ flex: 1 }} />
                </div>
                <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Al alcanzar el límite, los agentes dejan de responder para este cliente.</p>
              </div>
            </>
          )}

          {/* ── Equipo ── */}
          {allUsers.length > 0 && (
            <>
              <SectionHeading>Equipo asignado</SectionHeading>
              <p style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10, marginTop: -4 }}>
                Seleccioná qué miembros podrán ver y trabajar con este cliente.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {allUsers.map(u => {
                  const sel = selectedUserIds.has(u.id);
                  return (
                    <label key={u.id} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 12px", borderRadius: "var(--radius-md)",
                      background: sel ? "rgba(11,0,249,0.06)" : "var(--bg-elevated)",
                      border: `1px solid ${sel ? "var(--accent)" : "var(--border)"}`,
                      cursor: "pointer", transition: "background 0.12s, border-color 0.12s",
                    }}>
                      <input
                        type="checkbox"
                        checked={sel}
                        onChange={() => toggleUser(u.id)}
                        style={{ accentColor: "var(--accent)", width: 14, height: 14, flexShrink: 0 }}
                      />
                      {u.avatar ? (
                        <img src={u.avatar} alt="" style={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0 }} />
                      ) : (
                        <div style={{
                          width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                          background: "var(--bg-hover)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 600, color: "var(--text-secondary)",
                        }}>
                          {u.name?.[0]?.toUpperCase() || "?"}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {u.name}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {u.email}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}

            </div>

        {/* Footer */}
        <div style={{
          padding: "16px 24px", borderTop: "1px solid var(--border)",
          display: "flex", gap: 10, justifyContent: "flex-end", background: "var(--bg-elevated)",
        }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving
              ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Creando...</>
              : "Crear cliente"}
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .new-client-overlay {
            align-items: flex-end !important;
            padding: 0 !important;
          }
          .new-client-modal {
            margin: 0 !important;
            border-radius: var(--radius-lg) var(--radius-lg) 0 0 !important;
            max-width: 100% !important;
          }
          .new-client-modal > div:nth-child(2) {
            max-height: 75vh !important;
          }
          .modal-2col { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
